import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Calendar, User } from "lucide-react";
import { IntegrationIcon } from "../../IntegrationIcon/IntegrationIcon";
import { Badge } from "../../ui/badge";

import type { Issue } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Issue };

type IssueCardProps = Issue & BaseEntityCardProps;

// Avatar component for IssueCard
export function IssueCardAvatar({
  platform,
  className,
  style,
}: Partial<IssueCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <IntegrationIcon platform={platform} className="h-8 w-8" />
    </BaseEntityCardIcon>
  );
}

export function IssueCard({
  platform,
  summary,
  description = "",
  status,
  priority,
  project_name,
  assignee,
  updated_at,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: IssueCardProps) {
  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const descriptionPreview =
    description && description.length > 100
      ? `${description.toString().substring(0, 100)}...`
      : description.toString();

  // Map priority to color - subtle styling
  const getPriorityColor = (priorityValue: string) => {
    const lowerPriority = priorityValue.toLowerCase();
    if (
      lowerPriority.includes("high") ||
      lowerPriority.includes("critical") ||
      lowerPriority === "1"
    ) {
      return "text-lc-blocked dark:text-lc-blocked border-red-600/20";
    } else if (lowerPriority.includes("medium") || lowerPriority === "2") {
      return "text-lc-warning dark:text-lc-warning border-amber-600/20";
    } else if (lowerPriority.includes("low") || lowerPriority === "3") {
      return "text-teal dark:text-teal border-rust/20";
    }
    return "text-muted-foreground";
  };

  // Map status to color - subtle text-only styling like detail view
  const getStatusColor = (statusValue: string) => {
    const lowerStatus = statusValue.toLowerCase();
    if (lowerStatus === "open") {
      return "text-lc-active dark:text-lc-active border-green-600/20";
    } else if (lowerStatus === "closed") {
      return "text-lc-blocked dark:text-lc-blocked border-red-600/20";
    } else if (
      lowerStatus.includes("done") ||
      lowerStatus.includes("complete") ||
      lowerStatus.includes("resolved")
    ) {
      return "text-lc-active dark:text-lc-active border-green-600/20";
    } else if (
      lowerStatus.includes("progress") ||
      lowerStatus.includes("review")
    ) {
      return "text-teal dark:text-teal border-rust/20";
    } else if (lowerStatus.includes("block") || lowerStatus.includes("hold")) {
      return "text-lc-blocked dark:text-lc-blocked border-red-600/20";
    } else if (
      lowerStatus.includes("backlog") ||
      lowerStatus.includes("todo")
    ) {
      return "text-muted-foreground";
    }
    return "text-muted-foreground";
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <IssueCardAvatar platform={platform} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{summary}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            <Badge
              variant="outline"
              className={cn("text-xs", getStatusColor(status))}
            >
              {status}
            </Badge>

            {assignee?.name && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>{assignee.name}</span>
              </div>
            )}

            {priority && priority.toLowerCase() !== "none" && (
              <Badge
                variant="outline"
                className={cn("text-xs", getPriorityColor(priority))}
              >
                {priority}
              </Badge>
            )}

            {project_name && (
              <Badge variant="outline" className="text-xs">
                {project_name}
              </Badge>
            )}
          </BaseEntityCardTags>

          {formattedUpdatedDate && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3 w-3" />}
              >
                Updated {formattedUpdatedDate}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
